<template>
  <div v-if="showSessionStatus" class="session-status" :class="{ 'mobile': isMobile }">
    <div class="session-info">
      <div class="session-time">
        <span class="label">Session Time:</span>
        <span class="time" :class="{ 'warning': isWarning, 'critical': isCritical }">
          {{ formatTime(remainingTime) }}
        </span>
      </div>
      <div class="session-progress">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${progressPercentage}%` }"
            :class="{ 'warning': isWarning, 'critical': isCritical }"
          ></div>
        </div>
      </div>
      <button 
        @click="extendSession" 
        class="extend-btn"
        :disabled="isExtending"
        :class="{ 'mobile-btn': isMobile }"
      >
        <span v-if="isExtending" class="loading-spinner"></span>
        {{ isExtending ? 'Extending...' : 'Extend Session' }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import sessionManager from '@/services/session';
import SESSION_CONFIG from '@/config/session';

export default {
  name: 'SessionStatus',
  props: {
    system: {
      type: String,
      required: true,
      validator: (value) => SESSION_CONFIG.SUPPORTED_SYSTEMS.includes(value)
    },
    showAlways: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const store = useStore();
    const toast = useToast();
    const remainingTime = ref(0);
    const isExtending = ref(false);
    const updateInterval = ref(null);
    const isMobile = ref(false);

    // Detect mobile device
    const detectMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const isAuthenticated = computed(() => 
      store.getters.isAuthenticated(props.system)
    );

    const showSessionStatus = computed(() => {
      if (!isAuthenticated.value) return false;
      if (props.showAlways) return true;
      // Show when less than threshold remaining
      return remainingTime.value < SESSION_CONFIG.STATUS.SHOW_THRESHOLD;
    });

    const progressPercentage = computed(() => {
      const totalTime = SESSION_CONFIG.SESSION_TIMEOUT;
      return Math.max(0, Math.min(100, (remainingTime.value / totalTime) * 100));
    });

    const isWarning = computed(() => {
      return remainingTime.value < SESSION_CONFIG.STATUS.WARNING_THRESHOLD && 
             remainingTime.value > SESSION_CONFIG.STATUS.CRITICAL_THRESHOLD;
    });

    const isCritical = computed(() => {
      return remainingTime.value <= SESSION_CONFIG.STATUS.CRITICAL_THRESHOLD;
    });

    const updateRemainingTime = () => {
      if (isAuthenticated.value) {
        const newTime = sessionManager.getRemainingTime(props.system);
        // Only update if there's a significant change (prevents unnecessary re-renders)
        if (Math.abs(newTime - remainingTime.value) >= 1000) {
          remainingTime.value = newTime;
        }
      }
    };

    const formatTime = (milliseconds) => {
      if (milliseconds <= 0) return '0:00';
      
      const minutes = Math.floor(milliseconds / 60000);
      const seconds = Math.floor((milliseconds % 60000) / 1000);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const extendSession = async () => {
      if (isExtending.value) return;
      
      isExtending.value = true;
      try {
        await nextTick(); // Ensure UI updates
        sessionManager.extendSession(props.system);
        updateRemainingTime();
        
        // Show success message
        toast('Session extended successfully!', { 
          type: 'success',
          timeout: 3000,
          position: 'top-right'
        });
      } catch (error) {
        console.error('Failed to extend session:', error);
        toast('Failed to extend session', { 
          type: 'error',
          timeout: 4000,
          position: 'top-right'
        });
      } finally {
        isExtending.value = false;
      }
    };

    onMounted(() => {
      isMobile.value = detectMobile();
      updateRemainingTime();
      
      // Update at configurable intervals for better performance
      updateInterval.value = setInterval(updateRemainingTime, SESSION_CONFIG.PERFORMANCE.UI_UPDATE_INTERVAL);
    });

    onUnmounted(() => {
      if (updateInterval.value) {
        clearInterval(updateInterval.value);
      }
    });

    return {
      remainingTime,
      isExtending,
      showSessionStatus,
      progressPercentage,
      isWarning,
      isCritical,
      formatTime,
      extendSession,
      isMobile
    };
  }
};
</script>

<style scoped>
.session-status {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  transition: all 0.3s ease;
}

.session-status.mobile {
  top: 10px;
  right: 10px;
  left: 10px;
  min-width: auto;
  max-width: none;
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.session-time {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.label {
  color: #6b7280;
  font-weight: 500;
}

.time {
  font-weight: 600;
  color: #374151;
  transition: color 0.3s ease;
}

.time.warning {
  color: #f59e0b;
}

.time.critical {
  color: #ef4444;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.session-progress {
  width: 100%;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background-color: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #10b981;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.progress-fill.warning {
  background-color: #f59e0b;
}

.progress-fill.critical {
  background-color: #ef4444;
  animation: progress-pulse 1.5s infinite;
}

@keyframes progress-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

.extend-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.extend-btn:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.extend-btn:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.extend-btn.mobile-btn {
  padding: 8px 16px;
  font-size: 14px;
}

.loading-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive design */
@media (max-width: 768px) {
  .session-status {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
  
  .session-time {
    font-size: 13px;
  }
  
  .extend-btn {
    padding: 8px 16px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .session-status {
    padding: 10px;
  }
  
  .session-time {
    font-size: 12px;
  }
  
  .extend-btn {
    padding: 10px 20px;
    font-size: 16px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .session-status {
    background: #1f2937;
    border-color: #374151;
    color: #f9fafb;
  }
  
  .label {
    color: #9ca3af;
  }
  
  .time {
    color: #f9fafb;
  }
  
  .progress-bar {
    background-color: #374151;
  }
}
</style>
